'use client';

import type { FC } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { useTipTapEditor } from '@/hooks/annotations/useTipTapEditor';
import { cn } from '@/libs/tailwind';
import {
  createEditorAttributes,
  createReadOnlyExtensions,
  createTipTapWrapper,
  sharedParseOptions,
} from './tiptap-config';
import { useTipTapEffects } from './use-tiptap-effects';

export type TipTapReadOnlyProps = {
  value: string;
  className?: string;
  formatted?: boolean;
};

/**
 * TipTapReadOnly is a read-only version of the TipTap editor that ensures
 * consistent rendering between what users see while editing and what gets submitted.
 * This component is used to display formatted content in a read-only state, maintaining
 * the exact same styling and formatting as the editable version. It's particularly useful
 * for previewing content or displaying submitted text while preserving all formatting,
 * including markdown, mathematics, and custom styling.
 */

export const TipTapReadOnly: FC<TipTapReadOnlyProps> = ({
  value,
  className,
  formatted = true,
}) => {
  const { setEditor } = useTipTapEditor();
  const wrapperClasses = createTipTapWrapper(className);

  const editor = useEditor({
    extensions: createReadOnlyExtensions(),
    content: value,
    editable: false,
    parseOptions: sharedParseOptions,
    editorProps: {
      attributes: createEditorAttributes('[&_p:not(:last-child)]:mb-4'),
    },
  });

  // Use shared effects hook
  useTipTapEffects(editor, value, setEditor);

  if (!formatted) {
    return (
      <pre className={cn('whitespace-pre-wrap text-sm', className)}>
        {value}
      </pre>
    );
  }

  if (!editor) {
    return null;
  }

  return (
    <div className={wrapperClasses.wrapper}>
      <div className={wrapperClasses.container}>
        <div className={wrapperClasses.content}>
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
};
